import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  useQuery,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFarm } from "@/context/FarmContext";

import { PageHeader } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Users,
  Building2,
  Plus,
  Search,
  Mail,
  Phone,
  Tractor,
  ChevronRight,
  BriefcaseBusiness,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";

import { toast } from "sonner";


const EMPTY_CLIENT = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  notes: "",
};


const EMPTY_FARM = {
  name: "",
  grazing_area: "",
  cow_numbers: "",
  target_milk_yield: "",
  intake_per_cow: "",
  herd_type: "dairy",
  currency: "EUR",
};


export default function ConsultantDashboard() {
  const navigate =
    useNavigate();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const {
    user,
  } = useAuth();

  const {
    selectFarm,
    refresh: refreshFarms,
  } = useFarm();


  const selectedClientId =
    searchParams.get(
      "client"
    );


  const [
    search,
    setSearch,
  ] = useState("");


  const [
    showNewClient,
    setShowNewClient,
  ] = useState(false);


  const [
    showNewFarm,
    setShowNewFarm,
  ] = useState(false);


  const [
    savingClient,
    setSavingClient,
  ] = useState(false);


  const [
    savingFarm,
    setSavingFarm,
  ] = useState(false);


  const [
    newClient,
    setNewClient,
  ] = useState(
    EMPTY_CLIENT
  );


  const [
    newFarm,
    setNewFarm,
  ] = useState(
    EMPTY_FARM
  );


  const {
    data: clients = [],
    isLoading: clientsLoading,
    refetch: refetchClients,
  } = useQuery({
    queryKey: [
      "consultant-clients",
    ],

    queryFn: async () => {
      const {
        data,
      } = await api.get(
        "/consultant/clients"
      );

      return data;
    },
  });


  const {
    data: selectedClient,
    isLoading: clientLoading,
    refetch: refetchSelectedClient,
  } = useQuery({
    queryKey: [
      "consultant-client",
      selectedClientId,
    ],

    enabled:
      Boolean(
        selectedClientId
      ),

    queryFn: async () => {
      const {
        data,
      } = await api.get(
        `/consultant/clients/${selectedClientId}`
      );

      return data;
    },
  });


  const filteredClients =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      if (!term) {
        return clients;
      }

      return clients.filter(
        (client) => {
          const values = [
            client.name,
            client.business_name,
            client.email,
            client.phone,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return values.includes(
            term
          );
        }
      );
    }, [
      clients,
      search,
    ]);


  const totalFarms =
    clients.reduce(
      (
        total,
        client
      ) =>
        total +
        (
          client.farm_count ||
          0
        ),
      0
    );


  const createClient =
    async () => {
      if (
        !newClient.name.trim()
      ) {
        toast.error(
          "Client name is required."
        );

        return;
      }


      setSavingClient(
        true
      );


      try {
        const {
          data,
        } = await api.post(
          "/consultant/clients",
          {
            name:
              newClient.name.trim(),

            business_name:
              newClient.business_name.trim() ||
              null,

            email:
              newClient.email.trim() ||
              null,

            phone:
              newClient.phone.trim() ||
              null,

            notes:
              newClient.notes.trim() ||
              null,
          }
        );


        toast.success(
          "Client created."
        );


        setNewClient(
          EMPTY_CLIENT
        );


        setShowNewClient(
          false
        );


        await refetchClients();


        setSearchParams({
          client:
            data.id,
        });

      } catch (error) {
        const detail =
          error?.response?.data?.detail;


        toast.error(
          typeof detail ===
            "string"
            ? detail
            : detail?.message ||
              "Could not create client."
        );

      } finally {
        setSavingClient(
          false
        );
      }
    };


  const createFarm =
    async () => {
      if (
        !selectedClientId
      ) {
        return;
      }


      if (
        !newFarm.name.trim()
      ) {
        toast.error(
          "Farm name is required."
        );

        return;
      }


      setSavingFarm(
        true
      );


      try {
        const {
          data,
        } = await api.post(
          `/consultant/clients/${selectedClientId}/farms`,
          {
            name:
              newFarm.name.trim(),

            grazing_area:
              Number(
                newFarm.grazing_area
              ) || 0,

            cow_numbers:
              Number(
                newFarm.cow_numbers
              ) || 0,

            target_milk_yield:
              Number(
                newFarm.target_milk_yield
              ) || 0,

            intake_per_cow:
              Number(
                newFarm.intake_per_cow
              ) || 0,

            herd_type:
              newFarm.herd_type,

            currency:
              newFarm.currency,
          }
        );


        toast.success(
          "Client farm created."
        );


        setNewFarm(
          EMPTY_FARM
        );


        setShowNewFarm(
          false
        );


        await Promise.all([
          refetchSelectedClient(),
          refetchClients(),
          refreshFarms(),
        ]);


        selectFarm(
          data.id
        );

      } catch (error) {
        const detail =
          error?.response?.data?.detail;


        toast.error(
          typeof detail ===
            "string"
            ? detail
            : detail?.message ||
              "Could not create client farm."
        );

      } finally {
        setSavingFarm(
          false
        );
      }
    };


  const openFarm =
    async (
      farm
    ) => {
      try {
        await refreshFarms();

        selectFarm(
          farm.id
        );

        navigate(
          "/app"
        );

      } catch (error) {
        console.error(
          error
        );

        toast.error(
          "Could not open farm."
        );
      }
    };


  useEffect(() => {
    if (
      user?.role ===
      "admin"
    ) {
      return;
    }

    if (
      user?.subscription_plan !==
        "advisor" &&
      user?.subscription_plan !==
        "consultant"
    ) {
      navigate(
        "/app",
        {
          replace: true,
        }
      );
    }
  }, [
    user,
    navigate,
  ]);


  return (
    <div>

      <PageHeader
        title="Consultant Dashboard"
        subtitle="Manage client farms and professional Farm Hand workflows."
      />


      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

        <Card className="p-5">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-sm text-muted-foreground">
                Clients
              </p>

              <p className="font-display text-3xl font-bold text-[hsl(148,38%,16%)] mt-1">
                {clients.length}
              </p>

            </div>


            <div className="rounded-full bg-primary/10 p-3">

              <Users className="h-5 w-5 text-primary" />

            </div>

          </div>

        </Card>


        <Card className="p-5">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-sm text-muted-foreground">
                Client farms
              </p>

              <p className="font-display text-3xl font-bold text-[hsl(148,38%,16%)] mt-1">
                {totalFarms}
              </p>

            </div>


            <div className="rounded-full bg-primary/10 p-3">

              <Tractor className="h-5 w-5 text-primary" />

            </div>

          </div>

        </Card>


        <Card className="p-5">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-sm text-muted-foreground">
                Access
              </p>

              <p className="font-display text-xl font-bold text-[hsl(148,38%,16%)] mt-1">

                {user?.role ===
                "admin"
                  ? "Administrator"
                  : "Consultant"}

              </p>

            </div>


            <div className="rounded-full bg-primary/10 p-3">

              <BriefcaseBusiness className="h-5 w-5 text-primary" />

            </div>

          </div>

        </Card>

      </div>


      {!selectedClientId ? (

        <>
          <Card className="p-5 mb-6">

            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">

              <div className="relative flex-1 max-w-lg">

                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                <Input
                  value={
                    search
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setSearch(
                        event.target.value
                      )
                  }
                  placeholder="Search clients..."
                  className="pl-9"
                />

              </div>


              <Button
                className="rounded-full"
                onClick={() =>
                  setShowNewClient(
                    !showNewClient
                  )
                }
              >

                <Plus className="h-4 w-4 mr-2" />

                Add Client

              </Button>

            </div>


            {showNewClient && (

              <div className="border-t mt-5 pt-5">

                <h3 className="font-display font-bold text-lg text-[hsl(148,38%,16%)] mb-4">
                  New Client
                </h3>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <Input
                    placeholder="Client name"
                    value={
                      newClient.name
                    }
                    onChange={
                      (
                        event
                      ) =>
                        setNewClient(
                          {
                            ...newClient,
                            name:
                              event.target.value,
                          }
                        )
                    }
                  />


                  <Input
                    placeholder="Farm or business name"
                    value={
                      newClient.business_name
                    }
                    onChange={
                      (
                        event
                      ) =>
                        setNewClient(
                          {
                            ...newClient,
                            business_name:
                              event.target.value,
                          }
                        )
                    }
                  />


                  <Input
                    type="email"
                    placeholder="Email"
                    value={
                      newClient.email
                    }
                    onChange={
                      (
                        event
                      ) =>
                        setNewClient(
                          {
                            ...newClient,
                            email:
                              event.target.value,
                          }
                        )
                    }
                  />


                  <Input
                    placeholder="Phone"
                    value={
                      newClient.phone
                    }
                    onChange={
                      (
                        event
                      ) =>
                        setNewClient(
                          {
                            ...newClient,
                            phone:
                              event.target.value,
                          }
                        )
                    }
                  />

                </div>


                <textarea
                  className="mt-4 flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Notes"
                  value={
                    newClient.notes
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setNewClient(
                        {
                          ...newClient,
                          notes:
                            event.target.value,
                        }
                      )
                  }
                />


                <div className="flex justify-end gap-2 mt-4">

                  <Button
                    variant="outline"
                    onClick={() =>
                      setShowNewClient(
                        false
                      )
                    }
                  >
                    Cancel
                  </Button>


                  <Button
                    disabled={
                      savingClient
                    }
                    onClick={
                      createClient
                    }
                  >

                    {savingClient
                      ? "Saving…"
                      : "Create Client"}

                  </Button>

                </div>

              </div>

            )}

          </Card>


          <Card className="overflow-hidden">

            <div className="p-5 border-b">

              <div className="flex items-center gap-2">

                <Building2 className="h-5 w-5 text-primary" />

                <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                  Clients
                </h2>

              </div>

            </div>


            {clientsLoading ? (

              <div className="p-8 text-center text-muted-foreground">
                Loading clients…
              </div>

            ) : filteredClients.length ===
              0 ? (

              <div className="p-10 text-center">

                <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />

                <p className="font-semibold text-[hsl(148,38%,16%)]">
                  No clients yet
                </p>

                <p className="text-sm text-muted-foreground mt-1">
                  Add your first client to begin managing their Farm Hand workspace.
                </p>

              </div>

            ) : (

              <div className="divide-y">

                {filteredClients.map(
                  (
                    client
                  ) => (

                    <button
                      key={
                        client.id
                      }
                      type="button"
                      onClick={() =>
                        setSearchParams({
                          client:
                            client.id,
                        })
                      }
                      className="w-full p-5 text-left hover:bg-secondary/40 transition-colors"
                    >

                      <div className="flex items-center justify-between gap-4">

                        <div className="min-w-0">

                          <div className="flex items-center gap-2">

                            <p className="font-semibold text-[hsl(148,38%,16%)]">
                              {client.name}
                            </p>


                            {client.business_name && (

                              <span className="text-xs text-muted-foreground">
                                {client.business_name}
                              </span>

                            )}

                          </div>


                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">

                            {client.email && (

                              <span className="flex items-center gap-1">

                                <Mail className="h-3.5 w-3.5" />

                                {client.email}

                              </span>

                            )}


                            {client.phone && (

                              <span className="flex items-center gap-1">

                                <Phone className="h-3.5 w-3.5" />

                                {client.phone}

                              </span>

                            )}


                            <span className="flex items-center gap-1">

                              <Tractor className="h-3.5 w-3.5" />

                              {client.farm_count ||
                                0}{" "}
                              {
                                (
                                  client.farm_count ||
                                  0
                                ) === 1
                                  ? "farm"
                                  : "farms"
                              }

                            </span>

                          </div>

                        </div>


                        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />

                      </div>

                    </button>

                  )
                )}

              </div>

            )}

          </Card>
        </>

      ) : (

        <>
          <div className="mb-4">

            <Button
              variant="ghost"
              onClick={() => {
                setSearchParams({});
                setShowNewFarm(false);
              }}
            >

              <ArrowLeft className="h-4 w-4 mr-2" />

              Back to Clients

            </Button>

          </div>


          {clientLoading ? (

            <Card className="p-8 text-center text-muted-foreground">
              Loading client…
            </Card>

          ) : selectedClient ? (

            <>
              <Card className="p-6 mb-6">

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">

                  <div>

                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                      Client
                    </p>

                    <h2 className="font-display text-2xl font-bold text-[hsl(148,38%,16%)] mt-1">
                      {selectedClient.name}
                    </h2>


                    {selectedClient.business_name && (

                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedClient.business_name}
                      </p>

                    )}


                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">

                      {selectedClient.email && (

                        <span className="flex items-center gap-1">

                          <Mail className="h-4 w-4" />

                          {selectedClient.email}

                        </span>

                      )}


                      {selectedClient.phone && (

                        <span className="flex items-center gap-1">

                          <Phone className="h-4 w-4" />

                          {selectedClient.phone}

                        </span>

                      )}

                    </div>

                  </div>


                  <Button
                    className="rounded-full"
                    onClick={() =>
                      setShowNewFarm(
                        !showNewFarm
                      )
                    }
                  >

                    <Plus className="h-4 w-4 mr-2" />

                    Add Farm

                  </Button>

                </div>


                {showNewFarm && (

                  <div className="border-t mt-6 pt-6">

                    <h3 className="font-display font-bold text-lg text-[hsl(148,38%,16%)] mb-4">
                      New Client Farm
                    </h3>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                      <Input
                        placeholder="Farm name"
                        value={
                          newFarm.name
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                name:
                                  event.target.value,
                              }
                            )
                        }
                      />


                      <Input
                        type="number"
                        placeholder="Grazing area (ha)"
                        value={
                          newFarm.grazing_area
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                grazing_area:
                                  event.target.value,
                              }
                            )
                        }
                      />


                      <Input
                        type="number"
                        placeholder="Cow numbers"
                        value={
                          newFarm.cow_numbers
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                cow_numbers:
                                  event.target.value,
                              }
                            )
                        }
                      />


                      <Input
                        type="number"
                        placeholder="Target milk yield (L/cow/day)"
                        value={
                          newFarm.target_milk_yield
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                target_milk_yield:
                                  event.target.value,
                              }
                            )
                        }
                      />


                      <Input
                        type="number"
                        placeholder="Target intake (kg DM/cow/day)"
                        value={
                          newFarm.intake_per_cow
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                intake_per_cow:
                                  event.target.value,
                              }
                            )
                        }
                      />


                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={
                          newFarm.currency
                        }
                        onChange={
                          (
                            event
                          ) =>
                            setNewFarm(
                              {
                                ...newFarm,
                                currency:
                                  event.target.value,
                              }
                            )
                        }
                      >

                        <option value="EUR">
                          EUR (€)
                        </option>

                        <option value="GBP">
                          GBP (£)
                        </option>

                      </select>

                    </div>


                    <div className="flex justify-end gap-2 mt-4">

                      <Button
                        variant="outline"
                        onClick={() =>
                          setShowNewFarm(
                            false
                          )
                        }
                      >
                        Cancel
                      </Button>


                      <Button
                        disabled={
                          savingFarm
                        }
                        onClick={
                          createFarm
                        }
                      >

                        {savingFarm
                          ? "Creating…"
                          : "Create Farm"}

                      </Button>

                    </div>

                  </div>

                )}

              </Card>


              <Card className="overflow-hidden">

                <div className="p-5 border-b">

                  <div className="flex items-center gap-2">

                    <Tractor className="h-5 w-5 text-primary" />

                    <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                      Client Farms
                    </h2>

                  </div>

                </div>


                {!selectedClient.farms?.length ? (

                  <div className="p-10 text-center">

                    <Tractor className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />

                    <p className="font-semibold text-[hsl(148,38%,16%)]">
                      No farms yet
                    </p>

                    <p className="text-sm text-muted-foreground mt-1">
                      Add this client's first farm to start using Farm Hand.
                    </p>

                  </div>

                ) : (

                  <div className="divide-y">

                    {selectedClient.farms.map(
                      (
                        farm
                      ) => (

                        <div
                          key={
                            farm.id
                          }
                          className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                        >

                          <div>

                            <p className="font-semibold text-[hsl(148,38%,16%)]">
                              {farm.name}
                            </p>


                            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">

                              <span>
                                {farm.grazing_area ||
                                  0} ha
                              </span>

                              <span>
                                {farm.cow_numbers ||
                                  0} cows
                              </span>

                              <span>
                                {farm.target_milk_yield ||
                                  0} L/cow/day
                              </span>

                            </div>

                          </div>


                          <Button
                            variant="outline"
                            className="rounded-full"
                            onClick={() =>
                              openFarm(
                                farm
                              )
                            }
                          >

                            <ExternalLink className="h-4 w-4 mr-2" />

                            Open Farm

                          </Button>

                        </div>

                      )
                    )}

                  </div>

                )}

              </Card>
            </>

          ) : (

            <Card className="p-8 text-center text-muted-foreground">
              Client not found.
            </Card>

          )}
        </>

      )}

    </div>
  );
}
